import { Canvas } from '@react-three/fiber'
import { KeyboardControls, useProgress } from '@react-three/drei'
import { Suspense, useEffect, useMemo, lazy } from 'react'
import { ACESFilmicToneMapping, SRGBColorSpace } from 'three'
import { useAppStore } from '@/core/store'
import { QUALITY_PROFILES } from '@/core/constants'
import { MainMenu } from '@/components/ui/MainMenu'
import { HUD } from '@/components/ui/HUD'
import { LoadingScreen } from '@/components/ui/LoadingScreen'
import { PostFX } from '@/components/environment/PostFX'
import { FpsTracker } from '@/components/environment/FpsTracker'
import { AutoDegrade } from '@/components/environment/AutoDegrade'
import { bindingsToKeymap } from '@/systems/keymap'

// 动态导入两个场景，主菜单不会拉取场景 chunk。
const ForestScene = lazy(() =>
  import('@/scenes/ForestScene').then((m) => ({ default: m.ForestScene }))
)
const OceanScene = lazy(() =>
  import('@/scenes/OceanScene').then((m) => ({ default: m.OceanScene }))
)

function ActiveScene() {
  const currentScene = useAppStore((s) => s.currentScene)
  if (currentScene === 'forest') return <ForestScene />
  if (currentScene === 'ocean') return <OceanScene />
  return null
}

function useEscToMenu() {
  const goToScene = useAppStore((s) => s.goToScene)
  const currentScene = useAppStore((s) => s.currentScene)
  useEffect(() => {
    if (currentScene === 'menu') return
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Escape') goToScene('menu')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [currentScene, goToScene])
}

export function App() {
  const currentScene = useAppStore((s) => s.currentScene)
  const tier = useAppStore((s) => s.settings.quality)
  const keyBindings = useAppStore((s) => s.keyBindings)
  const profile = QUALITY_PROFILES[tier]
  useEscToMenu()

  const showCanvas = currentScene !== 'menu'
  const keymap = useMemo(() => bindingsToKeymap(keyBindings), [keyBindings])

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: '#02060a' }}>
      {showCanvas && (
        <KeyboardControls map={keymap}>
          <Canvas
            shadows
            dpr={[1, profile.pixelRatio]}
            gl={{
              antialias: true,
              toneMapping: ACESFilmicToneMapping,
              outputColorSpace: SRGBColorSpace,
            }}
            camera={{ position: [12, 8, 12], fov: 60, near: 0.1, far: 800 }}
          >
            <Suspense fallback={null}>
              <ActiveScene />
              <PostFX />
              <FpsTracker />
              <AutoDegrade />
            </Suspense>
          </Canvas>
        </KeyboardControls>
      )}

      {currentScene === 'menu' && <MainMenu />}
      <HUD />
      <SuspendedLoading />
    </div>
  )
}

function SuspendedLoading() {
  const loading = useAppStore((s) => s.loading)
  const active = useProgress((s) => s.active)
  if (!loading && !active) return null
  return <LoadingScreen />
}

